import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, KeyRound, Trash2, Power, PowerOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  saas_user: "SaaS User",
  saas_manager: "SaaS Manager",
  saas_admin: "SaaS Admin",
  super_admin: "Super Admin",
};
const ROLE_DESCRIPTIONS = {
  saas_user: "Read-only across SaaS console.",
  saas_manager: "Read + mark payments / send dunning. Cannot edit plans or gateways.",
  saas_admin: "Full SaaS console. Cannot manage Super Admins.",
  super_admin: "God mode. Can manage all staff including other Super Admins.",
};
const ROLE_BADGE = {
  saas_user: "border-slate-200 bg-slate-50 text-slate-700",
  saas_manager: "border-sky-200 bg-sky-50 text-sky-700",
  saas_admin: "border-indigo-200 bg-indigo-50 text-indigo-700",
  super_admin: "border-amber-300 bg-amber-50 text-amber-800",
};
const EMPTY = { name: "", email: "", password: "", role: "saas_user", phone: "" };

export default function SuperAdminStaff() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const [pwdOpen, setPwdOpen] = useState(null); // user being reset
  const [pwd, setPwd] = useState("");

  const [confirmDel, setConfirmDel] = useState(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const myRole = me?.role || "";
  const canManageSuperAdmin = myRole === "super_admin";
  const canCreate = ["super_admin", "saas_admin"].includes(myRole);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/staff");
      setItems(data.items || []);
      setRoles(data.roles || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role, phone: u.phone || "" });
    setOpen(true);
  };

  const allowedRolesForMe = canManageSuperAdmin ? roles : roles.filter((r) => r !== "super_admin");

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/superadmin/staff/${editing.id}`, {
          name: form.name, phone: form.phone, role: form.role,
        });
        toast.success("Staff updated");
      } else {
        await api.post("/superadmin/staff", form);
        toast.success("Staff created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const toggleStatus = async (u) => {
    try {
      const newStatus = u.status === "active" ? "inactive" : "active";
      await api.patch(`/superadmin/staff/${u.id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Activated" : "Deactivated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const submitPwd = async () => {
    if (!pwd || pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/superadmin/staff/${pwdOpen.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${pwdOpen.email}`);
      setPwdOpen(null); setPwd("");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/superadmin/staff/${confirmDel.id}`);
      toast.success("Staff deleted");
      setConfirmDel(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  // Show actions only for users this admin can actually manage
  const canManageRow = (u) => {
    if (myRole === "super_admin") return true;
    if (myRole === "saas_admin") return u.role !== "super_admin";
    return false;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">SaaS Console</div>
          <h1 className="font-heading text-3xl font-semibold">Internal Staff</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} member{items.length === 1 ? "" : "s"} · Steward Healthcare team</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid="staff-new">
            <Plus size={14} className="mr-2" /> Add staff
          </Button>
        )}
      </div>

      {/* Role legend */}
      <Card className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        {(roles.length ? roles : ["saas_user","saas_manager","saas_admin","super_admin"]).map((r) => (
          <div key={r} className="flex items-start gap-3">
            <Badge variant="outline" className={`mt-0.5 capitalize ${ROLE_BADGE[r] || ""}`}>
              {ROLE_LABELS[r] || r}
            </Badge>
            <div className="text-[11px] leading-snug text-ink-muted">{ROLE_DESCRIPTIONS[r] || ""}</div>
          </div>
        ))}
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ink-muted">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ink-muted">No staff yet.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="hover:bg-secondary/30" data-testid={`staff-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">
                  {u.name}
                  {u.id === me?.id && <span className="ml-2 rounded-full bg-leaf-50 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-leaf-700">You</span>}
                </td>
                <td className="px-4 py-3 text-xs">{u.email}</td>
                <td className="px-4 py-3 text-xs text-ink-muted">{u.phone || "—"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={`capitalize ${ROLE_BADGE[u.role] || ""}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={u.status === "active" ? "border-leaf/40 bg-leaf-50 text-leaf-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                    {u.status}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {canManageRow(u) && (
                      <>
                        <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(u)} data-testid={`staff-edit-${u.email}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" title="Reset password" onClick={() => { setPwdOpen(u); setPwd(""); }} data-testid={`staff-reset-${u.email}`}>
                          <KeyRound size={14} />
                        </Button>
                        {u.id !== me?.id && (
                          u.status === "active"
                            ? <Button size="sm" variant="ghost" title="Deactivate" onClick={() => toggleStatus(u)} data-testid={`staff-deactivate-${u.email}`}><PowerOff size={14} /></Button>
                            : <Button size="sm" variant="ghost" title="Activate" onClick={() => toggleStatus(u)} data-testid={`staff-activate-${u.email}`}><Power size={14} className="text-leaf" /></Button>
                        )}
                        {u.id !== me?.id && (
                          <Button size="sm" variant="ghost" title="Delete" onClick={() => setConfirmDel(u)} data-testid={`staff-delete-${u.email}`}><Trash2 size={14} className="text-rose-500" /></Button>
                        )}
                      </>
                    )}
                    {!canManageRow(u) && <span className="text-[10px] text-ink-muted">Read-only</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit staff" : "New staff member"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="staff-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input value={form.email} disabled={!!editing} onChange={(e) => set("email", e.target.value)} data-testid="staff-email-input" />
              </div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="staff-phone-input" /></div>
            </div>
            {!editing && (
              <div>
                <Label>Initial password</Label>
                <Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 8 characters" data-testid="staff-password-input" />
                <div className="mt-1 text-[10px] text-ink-muted">User can change this from Profile after first sign-in.</div>
              </div>
            )}
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedRolesForMe.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r] || r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-1 text-[10px] text-ink-muted">{ROLE_DESCRIPTIONS[form.role]}</div>
            </div>
            <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="staff-save">
              {editing ? "Save changes" : "Create staff"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdOpen} onOpenChange={(o) => !o && setPwdOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading"><KeyRound size={16} className="mr-2 inline" /> Reset password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Set a new password for <b>{pwdOpen?.email}</b>. Share it securely — they can change it from their Profile.</p>
            <div>
              <Label>New password</Label>
              <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Min 8 characters" data-testid="staff-pwd-input" autoFocus />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPwdOpen(null)}>Cancel</Button>
              <Button onClick={submitPwd} className="bg-brand hover:bg-brand-700" data-testid="staff-pwd-submit">
                <ShieldCheck size={14} className="mr-2" /> Update password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-rose-700">Delete staff member?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">This will <b>soft-delete</b> <b>{confirmDel?.email}</b>. They will lose access immediately.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="staff-delete-confirm">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
